import {
  coercePlantProvenance,
  decodeTaggedProps,
  type TaggedNode
} from '@axilon-apps/domains-ontology-ui-state'

export type NodeSourceFields = {
  sourceFile?: string
  sourcePage?: number
  sourceExcerpt?: string
}

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * Additions hydration ships plain props; compare diffs ship type-tagged
 * props. Prefer decode, fall back to the raw map when tags are absent.
 */
function readProps(properties: Record<string, unknown> | undefined): Record<string, unknown> {
  const raw = properties ?? {}
  try {
    return decodeTaggedProps(raw)
  } catch {
    return raw
  }
}

/**
 * Pick a coherent cite: file/page/excerpt must come from the same document.
 * Prefer flat `source_file` when present; only borrow page/excerpt from
 * provenance when its `source_file` matches (or when falling back to
 * provenance entirely).
 */
export function nodeSourceFields(node: TaggedNode): NodeSourceFields {
  const props = readProps(node.properties)
  const provenance = coercePlantProvenance(props['provenance'])
  const flatFile = asNonEmptyString(props['source_file'])
  const provenanceFile = asNonEmptyString(provenance?.source_file)

  if (flatFile) {
    const sameProvenanceFile = provenanceFile === flatFile
    return {
      sourceFile: flatFile,
      sourcePage:
        asFiniteNumber(props['source_page']) ??
        (sameProvenanceFile ? asFiniteNumber(provenance?.source_page) : undefined),
      sourceExcerpt:
        asNonEmptyString(props['source_excerpt']) ??
        (sameProvenanceFile ? asNonEmptyString(provenance?.source_excerpt) : undefined)
    }
  }

  if (!provenanceFile) return {}

  return {
    sourceFile: provenanceFile,
    sourcePage: asFiniteNumber(provenance?.source_page),
    sourceExcerpt: asNonEmptyString(provenance?.source_excerpt)
  }
}
